import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  PerkCategoryResponse,
  PerkCategoryRequest,
} from "@/types/generated/cms.generated";

// Fetch all perk categories
export function usePerkCategories() {
  return useQuery<PerkCategoryResponse[]>({
    queryKey: ["perk-categories"],
    queryFn: () => apiRequest("GET", "/perk-categories"),
  });
}

// Create perk category
export function useCreatePerkCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: PerkCategoryRequest) =>
      apiRequest("POST", "/perk-categories", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perk-categories"] });
      toast({
        title: "Category created",
        description: "Perk category has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Update perk category
export function useUpdatePerkCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PerkCategoryRequest }) =>
      apiRequest("PUT", `/perk-categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perk-categories"] });
      toast({
        title: "Category updated",
        description: "Perk category has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Delete perk category
export function useDeletePerkCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/perk-categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perk-categories"] });
      toast({
        title: "Category deleted",
        description: "Perk category has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete category. Please try again.",
        variant: "destructive",
      });
    },
  });
}
